import {
  extractTrajectorySeries,
  findTrajectoryTransonicRegion,
  findTrajectoryEnergyThreshold,
} from "./trajectory-chart.js";
import { resolveMuzzleVelocity } from "./mv-temperature.js";
import { parseVelocityBcBands } from "./velocity-bands.js";
import { prepareTrajectoryParameters } from "./wind.js";

export const RANGE_TABLE_MIN_TEMPERATURES = 3;
export const RANGE_TABLE_MAX_TEMPERATURES = 12;

function finiteNumber(value, label) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) throw new TypeError(`${label} must be a finite number.`);
  return numeric;
}

export function parseTemperatureList(value) {
  const tokens = String(value ?? "").trim().split(/[\s,;]+/).filter(Boolean);
  if (tokens.length === 0) {
    throw new TypeError("Enter at least three temperatures separated by commas or spaces.");
  }

  const temperatures = tokens.map((token) => finiteNumber(token, "Each temperature"));
  if (temperatures.some((temperature) => temperature < -40 || temperature > 140)) {
    throw new RangeError("Temperatures must be between -40 °F and 140 °F.");
  }

  const unique = [...new Set(temperatures)].sort((left, right) => left - right);
  if (unique.length < RANGE_TABLE_MIN_TEMPERATURES) {
    throw new RangeError("Enter at least three unique temperatures.");
  }
  if (unique.length > RANGE_TABLE_MAX_TEMPERATURES) {
    throw new RangeError(`Enter no more than ${RANGE_TABLE_MAX_TEMPERATURES} temperatures.`);
  }
  return unique;
}

export function sampledTemperatureIndices(count) {
  if (!Number.isInteger(count) || count < RANGE_TABLE_MIN_TEMPERATURES) return [];
  return [0, Math.floor(count / 2), count - 1];
}

function rangeKey(range) {
  return Number(range).toFixed(6);
}

function cloneParameters(parameters) {
  return new URLSearchParams(parameters);
}

function velocityBcMode(parameters) {
  const selected = parameters.get("bcm");
  if (["1", "2", "3"].includes(selected)) return selected;
  // Preserve range-table requests saved by the earlier checkbox-based band UI.
  if (parameters.has("bcp")) return parameters.has("bce") ? "3" : "1";
  if (String(parameters.get("bcb") || "").trim()) return "3";
  return "1";
}

function lateralLeadConfiguration(parameters) {
  const speedMph = finiteNumber(parameters.get("sp"), "Target speed");
  const rawDirection = finiteNumber(
    parameters.get("sa"), "Target direction of travel",
  );
  if (speedMph < 0 || speedMph > 100) {
    throw new RangeError("Target speed must be between 0 and 100 mph.");
  }
  if (rawDirection < 0 || rawDirection > 360) {
    throw new RangeError("Target direction of travel must be between 0° and 360°.");
  }
  return {
    speedMph,
    directionDegrees: rawDirection,
  };
}

function windConfiguration(parameters) {
  const components = {
    rangeMph: finiteNumber(parameters.get("wx"), "Range wind"),
    verticalMph: finiteNumber(parameters.get("wy"), "Vertical wind"),
    crossMph: finiteNumber(parameters.get("wz"), "Crosswind"),
  };
  if (Object.values(components).some((value) => value < -100 || value > 100)) {
    throw new RangeError("Each wind component must be between -100 and 100 mph.");
  }
  return components;
}

function trajectoryParameters(base, temperatureFahrenheit, muzzleVelocityFps,
  sampled, wind, lateralLead) {
  const parameters = cloneParameters(base);
  for (const name of ["temps", "mvt", "mvtb", "mvs"]) parameters.delete(name);
  parameters.set("tp", String(temperatureFahrenheit));
  parameters.set("mv", String(muzzleVelocityFps));
  // Wind changes the primary trajectory as well as horizontal correction.
  // Apply it to every temperature case; only target lead is limited to the
  // low/middle/high sampled cases used by the horizontal table groups.
  parameters.set("wx", String(wind.rangeMph));
  parameters.set("wy", String(wind.verticalMph));
  parameters.set("wz", String(wind.crossMph));
  parameters.set("sp", sampled ? String(lateralLead.speedMph) : "0");
  parameters.set("sa", sampled ? String(lateralLead.directionDegrees) : "0");
  return parameters;
}

async function mapWithConcurrency(values, concurrency, mapper) {
  const results = new Array(values.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < values.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(values[index], index);
    }
  }

  await Promise.all(Array.from(
    { length: Math.min(concurrency, values.length) }, () => worker(),
  ));
  return results;
}

function rangeNotice(result) {
  const section = result.sections?.find(({ title }) => title === "Trajectory Range Notice");
  if (!section) return null;
  const rows = section.tables?.flatMap(({ rows: tableRows }) => tableRows) || [];
  const reason = rows.find(({ cells }) => String(cells?.[0]).startsWith("Reason"));
  return reason?.cells?.[1] || "This trajectory ended before the requested maximum range.";
}

function compactCase(job, result) {
  if (result?.error) {
    throw new Error(`${job.temperatureFahrenheit} °F: ${result.error}`);
  }
  const series = extractTrajectorySeries(result);
  if (!series) {
    throw new Error(`${job.temperatureFahrenheit} °F: the trajectory engine returned no range samples.`);
  }
  return {
    temperatureFahrenheit: job.temperatureFahrenheit,
    muzzleVelocityFps: job.muzzleVelocityFps,
    notice: rangeNotice(result),
    series,
  };
}

function indicatorsForCase(trajectoryCase) {
  const { samples } = trajectoryCase.series;
  const transonicRegion = findTrajectoryTransonicRegion(samples);
  const fiveHundred = findTrajectoryEnergyThreshold(samples, 500);
  const threeFifty = findTrajectoryEnergyThreshold(samples, 350);
  return {
    transonicRegion,
    markers: [
      fiveHundred ? {
        kind: "energy-500",
        range: fiveHundred.range,
        threshold: fiveHundred.thresholdEnergy,
      } : null,
      threeFifty ? {
        kind: "energy-350",
        range: threeFifty.range,
        threshold: threeFifty.thresholdEnergy,
      } : null,
    ].filter(Boolean),
  };
}

function valuesByRange(trajectoryCase, keys) {
  return Object.fromEntries(trajectoryCase.series.samples.map((sample) => [
    rangeKey(sample.range),
    Object.fromEntries(keys.map((key) => [key, sample[key]])),
  ]));
}

export async function calculateRangeTable(formData, runTrajectory) {
  const base = prepareTrajectoryParameters(formData);
  const temperatures = parseTemperatureList(base.get("temps"));
  const mvTemperatureTable = base.get("mvtb");
  const sensitivityValue = base.get("mvs");
  const sensitivityFpsPerFahrenheit = sensitivityValue === null
      || sensitivityValue.trim() === ""
    ? 0
    : finiteNumber(sensitivityValue, "Muzzle-velocity temperature sensitivity");
  const selectedVelocityBcMode = velocityBcMode(base);
  const wind = windConfiguration(base);
  const lateralLead = lateralLeadConfiguration(base);
  const referenceTemperatureFahrenheit = 59;
  const referenceVelocityFps = resolveMuzzleVelocity(
    mvTemperatureTable, referenceTemperatureFahrenheit, 0,
  );
  base.set("bcrv", String(referenceVelocityFps));
  const adjustedVelocities = temperatures.map((temperature) => (
    resolveMuzzleVelocity(
      mvTemperatureTable, temperature, sensitivityFpsPerFahrenheit,
    )
  ));

  const sampleIndices = sampledTemperatureIndices(temperatures.length);
  const horizontalSampleRequired = Object.values(wind).some((value) => value !== 0)
    || base.has("sdc") || base.has("coc");
  const leadSampleRequired = lateralLead.speedMph !== 0;
  const sampledOutputRequired = horizontalSampleRequired || leadSampleRequired;
  const jobs = [
    ...temperatures.map((temperatureFahrenheit, index) => ({
      kind: "drop",
      index,
      temperatureFahrenheit,
      muzzleVelocityFps: adjustedVelocities[index],
      sampled: false,
    })),
    ...(sampledOutputRequired ? sampleIndices : []).map((temperatureIndex, index) => ({
      kind: "sample",
      index,
      temperatureIndex,
      temperatureFahrenheit: temperatures[temperatureIndex],
      muzzleVelocityFps: adjustedVelocities[temperatureIndex],
      sampled: true,
    })),
  ];

  const calculated = await mapWithConcurrency(jobs, 3, async (job) => {
    const parameters = trajectoryParameters(base, job.temperatureFahrenheit,
      job.muzzleVelocityFps, job.sampled, wind, lateralLead);
    const result = await runTrajectory(parameters,
      `${job.sampled ? "Wind/lateral-lead sample" : "Drop"} at ${job.temperatureFahrenheit} °F`);
    return { job, trajectoryCase: compactCase(job, result) };
  });
  const dropTrajectoryCases = calculated.filter(({ job }) => job.kind === "drop")
    .sort((left, right) => left.job.index - right.job.index)
    .map(({ trajectoryCase }) => trajectoryCase);
  const sampleTrajectoryCases = calculated.filter(({ job }) => job.kind === "sample")
    .sort((left, right) => left.job.index - right.job.index)
    .map(({ trajectoryCase }) => trajectoryCase);
  const dropCases = dropTrajectoryCases.map((trajectoryCase) => {
    const indicators = indicatorsForCase(trajectoryCase);
    return {
      temperatureFahrenheit: trajectoryCase.temperatureFahrenheit,
      muzzleVelocityFps: trajectoryCase.muzzleVelocityFps,
      notice: trajectoryCase.notice,
      transonicRegion: indicators.transonicRegion,
      markers: indicators.markers,
      values: valuesByRange(trajectoryCase, ["drop", "velocity", "energy"]),
    };
  });
  const sampledCases = sampleTrajectoryCases.map((trajectoryCase) => ({
    temperatureFahrenheit: trajectoryCase.temperatureFahrenheit,
    muzzleVelocityFps: trajectoryCase.muzzleVelocityFps,
    notice: trajectoryCase.notice,
    values: valuesByRange(trajectoryCase, ["windage", "lead"]),
  }));

  const ranges = [...new Map(dropTrajectoryCases.flatMap(({ series }) => (
    series.samples.map(({ range }) => [rangeKey(range), range])
  ))).values()].sort((left, right) => left - right);
  const firstDrop = dropTrajectoryCases[0]?.series;
  const firstSample = sampleTrajectoryCases[0]?.series;
  const velocityBandCount = selectedVelocityBcMode === "3"
    ? parseVelocityBcBands(base.get("bcb")).length : 0;

  return {
    kind: "range-table",
    title: "Temperature Range Table",
    error: null,
    ranges,
    dropCases,
    sampledCases,
    sourceUnits: {
      range: firstDrop?.rangeUnit || "(yards)",
      drop: firstDrop?.dropUnit || "",
      windage: firstSample?.windageUnit || "",
      lead: firstSample?.leadUnit || "",
    },
    referenceVelocityFps,
    referenceTemperatureFahrenheit,
    sensitivityFpsPerFahrenheit,
    velocityBcMode: selectedVelocityBcMode,
    velocityDependentBc: selectedVelocityBcMode !== "1",
    velocityBandCount,
    targetSpeedMph: lateralLead.speedMph,
    targetDirectionDegrees: lateralLead.directionDegrees,
    wind,
    horizontalSampleRequired,
    leadSampleRequired,
    notices: [
      ...dropCases.map((item) => item.notice),
      ...sampledCases.map((item) => item.notice),
    ].filter(Boolean),
  };
}

export function rangeTableValueAt(trajectoryCase, range) {
  return trajectoryCase?.values?.[rangeKey(range)] || null;
}

export function rangeTableMarkersAt(trajectoryCase, range, previousRange = null) {
  return (trajectoryCase?.markers || []).filter((marker) => (
    marker.range <= range && (previousRange === null || marker.range > previousRange)
  ));
}

export function rangeTableTransonicRegionAt(trajectoryCase, range,
  previousRange = null) {
  const region = trajectoryCase?.transonicRegion;
  const current = Number(range);
  const previous = previousRange === null ? current : Number(previousRange);
  if (!region || !Number.isFinite(current) || !Number.isFinite(previous)
    || !rangeTableValueAt(trajectoryCase, current)) return null;

  const start = region.entry.range;
  const end = region.exit.range;
  const low = Math.min(previous, current);
  const high = Math.max(previous, current);
  const overlaps = Math.max(low, start) < Math.min(high, end)
    || (current >= start && current <= end);
  if (!overlaps) return null;

  const boundaryInInterval = (boundary) => boundary.reached
    && boundary.range <= high
    && (previousRange === null ? boundary.range === current : boundary.range > low);
  return {
    region,
    entryBoundary: boundaryInInterval(region.entry),
    exitBoundary: boundaryInInterval(region.exit),
    beginsInside: region.startsInsideRegion && current === start,
    exitNotReached: region.endsInsideRegion && current === end,
  };
}
